import {
  LayoutDashboardIcon,
  UsersIcon,
  ServerIcon,
  DollarSignIcon,
  ActivityIcon,
  ShieldIcon,
  HelpCircleIcon,
  NetworkIcon,
  HardDriveIcon,
  ShoppingCartIcon,
  BarChart3Icon,
  UserIcon,
  StoreIcon,
  HandshakeIcon,
  AlertTriangleIcon,
  XIcon,
  RouterIcon,
  MenuIcon,
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'

import { ScrollArea, UserMenu, NotificationBell } from '@nebula/shared/components'
import { getCreditBalance, formatCurrency } from '@nebula/shared/services/creditAccountApi'
import { useAuthStore, usePortalStore } from '@nebula/shared/store'

const MainLayout = () => {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { theme, isPortalDomain } = usePortalStore()
  const drawerRef = useRef<HTMLDivElement>(null)

  const isCaptiveCustomer = user?.organization?.parent_id != null
  const displayName = user?.full_name || user?.email || 'Conta'
  const userInitial = displayName.charAt(0).toUpperCase()
  const organizationName = user?.organization?.name || 'Organização'

  const [showLowBalanceAlert, setShowLowBalanceAlert] = useState(false)
  const [_creditBalance, setCreditBalance] = useState<number>(0)
  const [availableCredit, setAvailableCredit] = useState<number>(0)
  const [currency, setCurrency] = useState<string>('BRL')
  const [alertDismissed, setAlertDismissed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const dismissedInSession = sessionStorage.getItem('lowBalanceAlertDismissed') === 'true'
    if (dismissedInSession) {
      setAlertDismissed(true)
      return
    }

    const fetchCreditBalance = async () => {
      try {
        const balance = await getCreditBalance()

        if (balance.has_credit_account && balance.balance !== null) {
          setCreditBalance(balance.balance)
          setAvailableCredit(balance.available_credit)
          setCurrency(balance.currency)

          const threshold = balance.low_balance_threshold ?? 100
          if (balance.available_credit < threshold && !alertDismissed) {
            setShowLowBalanceAlert(true)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar saldo de crédito:', error)
      }
    }

    fetchCreditBalance()
  }, [alertDismissed])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileMenuOpen) {
      return
    }

    const previousOverflow = document.body.style.overflow
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false)
      }
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleEscape)
    drawerRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [mobileMenuOpen])

  const handleDismissAlert = () => {
    setShowLowBalanceAlert(false)
    setAlertDismissed(true)
    sessionStorage.setItem('lowBalanceAlertDismissed', 'true')
  }

  const allMenuItems = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboardIcon,
      section: 'main',
      featureKey: 'dashboard' as const,
    },
    {
      path: '/marketplace',
      label: 'Marketplace',
      icon: ShoppingCartIcon,
      section: 'main',
      featureKey: 'marketplace' as const,
    },
    {
      path: '/instances',
      label: 'Instâncias',
      icon: ServerIcon,
      section: 'main',
      featureKey: 'instances' as const,
    },
    {
      path: '/monitoring',
      label: 'Monitoramento',
      icon: ActivityIcon,
      section: 'main',
      featureKey: 'monitoring' as const,
    },
    {
      path: '/backups',
      label: 'Backups',
      icon: ShieldIcon,
      section: 'main',
      featureKey: 'backups' as const,
    },
    {
      path: '/networking',
      label: 'Rede & Segurança',
      icon: NetworkIcon,
      section: 'advanced',
      featureKey: 'networking' as const,
    },
    {
      path: '/gateway',
      label: 'Cloud Gateway',
      icon: RouterIcon,
      section: 'advanced',
      featureKey: 'gateway' as const,
    },
    {
      path: '/storage',
      label: 'Storage',
      icon: HardDriveIcon,
      section: 'advanced',
      featureKey: 'storage' as const,
    },
    {
      path: '/analytics',
      label: 'Analytics',
      icon: BarChart3Icon,
      section: 'advanced',
      featureKey: 'analytics' as const,
    },
    {
      path: '/team',
      label: 'Equipe',
      icon: UsersIcon,
      section: 'management',
      featureKey: 'team' as const,
    },
    {
      path: '/billing',
      label: 'Cobrança',
      icon: DollarSignIcon,
      section: 'management',
      featureKey: 'billing' as const,
    },
    {
      path: '/resellers',
      label: 'Marketplace de Resellers',
      icon: StoreIcon,
      section: 'management',
      hideForCaptive: true, // Esconder para clientes cativos
    },
    {
      path: '/my-associations',
      label: 'Minhas Associações',
      icon: HandshakeIcon,
      section: 'management',
      hideForCaptive: true, // Esconder para clientes cativos
    },
    {
      path: '/account',
      label: 'Minha Conta',
      icon: UserIcon,
      section: 'management',
    },
    {
      path: '/support',
      label: 'Suporte',
      icon: HelpCircleIcon,
      section: 'management',
      featureKey: 'support' as const,
    },
  ]

  const menuItems = allMenuItems.filter((item) => {
    if (isCaptiveCustomer && item.hideForCaptive) {
      return false
    }
    // Filtrar por features habilitadas no portal
    if (isPortalDomain && theme && item.featureKey) {
      const enabled = theme.featuresEnabled[item.featureKey as keyof typeof theme.featuresEnabled]
      if (enabled === false) return false
    }
    return true
  })

  const menuSections = [
    {
      title: 'Principal',
      key: 'main',
      items: menuItems.filter((item) => item.section === 'main'),
    },
    {
      title: 'Recursos Avançados',
      key: 'advanced',
      items: menuItems.filter((item) => item.section === 'advanced'),
    },
    {
      title: 'Gestão',
      key: 'management',
      items: menuItems.filter((item) => item.section === 'management'),
    },
  ]

  const isMenuItemActive = (path: string) => {
    if (location.pathname === path) {
      return true
    }
    return path !== '/dashboard' && location.pathname.startsWith(`${path}/`)
  }

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <div
      className="flex size-full flex-col overflow-hidden bg-gray-900"
      style={{ backgroundColor: 'var(--portal-sidebar, #111827)' }}
    >
      <div className="flex flex-col border-b border-gray-800">
        <div className="flex h-16 items-center justify-between px-4">
          {isPortalDomain && theme?.logoUrl ? (
            <Link
              to="/dashboard"
              onClick={() => mobile && setMobileMenuOpen(false)}
              className="flex min-w-0 flex-1 items-center"
            >
              <img
                src={theme.logoUrl}
                alt={theme.portalName}
                className="max-h-12 w-full object-contain"
              />
            </Link>
          ) : (
            <Link
              to="/dashboard"
              onClick={() => mobile && setMobileMenuOpen(false)}
              className="flex items-center space-x-2"
            >
              <UsersIcon
                className="size-8"
                style={
                  isPortalDomain && theme ? { color: theme.primaryColor } : { color: '#a855f7' }
                }
              />
              <span className="text-xl font-bold text-white">
                {isPortalDomain && theme ? theme.portalName : 'Nebvla Customer'}
              </span>
            </Link>
          )}
          {mobile ? (
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded-lg p-2 text-gray-300 hover:bg-gray-800 hover:text-white"
              aria-label="Fechar menu"
            >
              <XIcon className="size-5" />
            </button>
          ) : null}
        </div>
      </div>

      <ScrollArea className="flex-1 px-4">
        <nav className="py-6">
          {menuSections.map((section) => (
            <div key={section.key} className="mb-7">
              <h3 className="mb-3 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon
                  const isActive = isMenuItemActive(item.path)

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => mobile && setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 rounded-lg px-3 py-2 transition-colors ${
                        isActive ? 'text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                      style={
                        isActive
                          ? {
                              backgroundColor:
                                isPortalDomain && theme ? theme.primaryColor : '#9333ea',
                            }
                          : undefined
                      }
                    >
                      <Icon className="size-5" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      <UserMenu user={user} logout={logout} profilePath="/account" />
    </div>
  )

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ backgroundColor: 'var(--portal-background, #F9FAFB)' }}
    >
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 md:flex">
        <SidebarContent />
      </aside>

      <div
        className={`fixed inset-0 z-50 md:hidden ${mobileMenuOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!mobileMenuOpen}
      >
        <button
          type="button"
          onClick={() => setMobileMenuOpen(false)}
          className={`absolute inset-0 bg-black/50 transition-opacity ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          aria-label="Fechar menu"
        />
        <div
          ref={drawerRef}
          tabIndex={-1}
          className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] transform transition-transform duration-200 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarContent mobile />
        </div>
      </div>

      <div className="min-h-screen md:pl-64">
        <div
          className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 md:hidden"
          style={{
            backgroundColor: 'var(--portal-top-bar, #FFFFFF)',
            borderColor: 'var(--portal-border, #E5E7EB)',
          }}
        >
          <div className="flex min-w-0 flex-1 items-center">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="shrink-0 rounded-lg p-2 text-gray-700 hover:bg-gray-100"
              aria-label="Abrir menu"
            >
              <MenuIcon className="size-5" />
            </button>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
              {organizationName}
            </span>
          </div>

          <Link to="/dashboard" className="flex shrink-0 items-center text-gray-900">
            {isPortalDomain && theme?.logoUrl ? (
              <img src={theme.logoUrl} alt={theme.portalName} className="h-7 object-contain" />
            ) : (
              <div className="flex items-center space-x-2">
                <UsersIcon
                  className="size-6"
                  style={
                    isPortalDomain && theme ? { color: theme.primaryColor } : { color: '#a855f7' }
                  }
                />
                <span className="text-sm font-semibold">
                  {isPortalDomain && theme ? theme.portalName : 'Nebvla Customer'}
                </span>
              </div>
            )}
          </Link>

          <div className="flex shrink-0 items-center gap-1.5">
            <NotificationBell portal="ec" />
            <Link
              to="/account"
              className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-700"
              aria-label="Minha conta"
            >
              {userInitial}
            </Link>
          </div>
        </div>

        <div
          className="sticky top-0 z-30 hidden h-12 items-center justify-between border-b border-gray-200 bg-white px-6 md:flex"
          style={{
            backgroundColor: 'var(--portal-top-bar, #FFFFFF)',
            borderColor: 'var(--portal-border, #E5E7EB)',
          }}
        >
          <div className="flex min-w-0 items-center">
            <span className="truncate text-sm font-medium text-gray-700">{organizationName}</span>
          </div>
          <div className="ml-3">
            <NotificationBell portal="ec" />
          </div>
        </div>

        {showLowBalanceAlert && (
          <div className="border-l-4 border-yellow-400 bg-yellow-50 p-3 shadow-sm sm:p-4">
            <div className="mx-auto flex max-w-7xl items-start justify-between gap-3 px-1 sm:px-2">
              <div className="flex min-w-0 items-start space-x-3">
                <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-yellow-600 sm:size-6" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-yellow-800">
                    Credito Disponivel Baixo
                  </h3>
                  <div className="mt-1 text-sm text-yellow-700">
                    <p>
                      Seu credito disponível e de{' '}
                      <strong>{formatCurrency(availableCredit, currency)}</strong>.
                      {availableCredit <= 0 && (
                        <span className="ml-1 font-semibold text-red-600">
                          Credito esgotado! Entre em contato com seu reseller.
                        </span>
                      )}
                      {availableCredit > 0 && (
                        <span className="ml-1">
                          Recomendamos solicitar mais saldo ao seu reseller para evitar interrupcao
                          nos servicos.
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="mt-2">
                    <Link
                      to="/billing"
                      className="text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
                    >
                      Ver detalhes de cobranca
                    </Link>
                  </div>
                </div>
              </div>
              <button
                onClick={handleDismissAlert}
                className="ml-2 shrink-0 rounded p-1 text-yellow-600 transition-colors hover:bg-yellow-100 hover:text-yellow-800"
                aria-label="Fechar alerta"
              >
                <XIcon className="size-5" />
              </button>
            </div>
          </div>
        )}

        <main className="min-h-[calc(100vh-3.5rem)]">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default MainLayout
